import { Keyword, UtilizationTag } from "@/types";
import { BulkMasterListMode, type BulkMasterListState } from "./types";

/**
 * "Landscaped" should only be surfaced (as a badge, or as a filter match) when
 * this keyword IS the Priority Search Phrase of its landscape (AUT-34) —
 * other keywords pulled in by that landscape can still be Tracked/A-B
 * Tested, but shouldn't claim to be "landscaped" themselves. Shared by
 * `TagCells.tsx` (badge rendering) and `keywordBankClient.ts` (the
 * Utilization filter dropdown) and by the landscape output's Filters panel
 * (`keyword-landscape-output/landscapeFilters.ts`) so they can't drift apart.
 */
export function matchesPrioritySearchPhrase(search: string, phrase: string | null | undefined): boolean {
  if (!phrase) return false;
  return search.trim().toLowerCase() === phrase.trim().toLowerCase();
}

export function isPrioritySearchPhrase(keyword: Keyword): boolean {
  return matchesPrioritySearchPhrase(keyword.search, keyword.landscape?.search_phrase);
}

/** The subset of a keyword's utilization tags that should actually be displayed/matched. */
export function getVisibleUtilizationTags(keyword: Keyword): UtilizationTag[] {
  const tags = keyword.utilizationTags ?? [];
  if (isPrioritySearchPhrase(keyword)) return tags;
  return tags.filter((tag) => tag !== UtilizationTag.LANDSCAPED);
}

export const getBulkMasterListState = (
  selectedIds: ReadonlySet<number>,
  allKeywords: Keyword[],
): BulkMasterListState | null => {
  if (!selectedIds.size) return null;

  const selectedKeywordItems = Array.from(selectedIds)
    .map((id) => allKeywords.find((k) => k.id === id))
    .filter((k): k is Keyword => k != null);

  if (
    selectedKeywordItems.length === 0 ||
    selectedKeywordItems.length !== selectedIds.size
  ) {
    return null;
  }

  const allInMaster = selectedKeywordItems.every(
    ({ is_master_list }) => is_master_list,
  );
  const noneInMaster = selectedKeywordItems.every(
    ({ is_master_list }) => !is_master_list,
  );

  if (noneInMaster) {
    return { mode: BulkMasterListMode.ADD_ALL, label: "Add to Master list" };
  }
  if (allInMaster) {
    return {
      mode: BulkMasterListMode.REMOVE_ALL,
      label: "Delete from Master list",
    };
  }
  return { mode: BulkMasterListMode.MIXED, label: "Update Master list" };
};

/** Formats a KPS factor score for display: "—" when absent, integer when whole, one decimal otherwise. */
export function formatScore(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}